import { HighlightText } from "@/components/MedicalChronology/HighlightText";
import { cn } from "@paribus/shared-ui";
import { ChevronDown, ChevronUp } from "lucide-react";
import React, { useState } from "react";

export interface SummaryBackgroundFieldProps {
  icon: React.ReactNode;
  iconColor: string;
  title: string;
  value: string | null | undefined;
  isEditMode: boolean;
  onChange: (value: string) => void;
  placeholder?: string;
  highlightQuery: string;
}

export const SummaryBackgroundField: React.FC<SummaryBackgroundFieldProps> = ({
  icon,
  iconColor,
  title,
  value,
  isEditMode,
  onChange,
  placeholder,
  highlightQuery,
}) => {
  const displayValue = value?.trim() ? value : null;
  const [isCollapsed, setIsCollapsed] = useState(false);
  const hasContent = !!displayValue;
  const viewFolded = !isEditMode && hasContent && isCollapsed;

  return (
    <div className="group rounded-xl border border-neutral-100 bg-card transition-all hover:border-neutral-200 hover:shadow-sm">
      <button
        type="button"
        onClick={() => !isEditMode && setIsCollapsed((v) => !v)}
        className={cn(
          "flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition-colors",
          !isEditMode && "cursor-pointer hover:bg-neutral-50/80",
          isEditMode && "cursor-default"
        )}
      >
        <div className="flex items-center gap-2.5">
          <div className={cn("flex items-center justify-center rounded-lg p-1.5", iconColor)}>
            {icon}
          </div>
          <span className="text-xs font-bold tracking-tight text-neutral-800">
            <HighlightText text={title} highlightQuery={highlightQuery} />
          </span>
          {!hasContent && !isEditMode && (
            <span className="ml-1 rounded-full bg-neutral-100 px-2 py-0.5 text-2xs font-medium text-neutral-400">
              Empty
            </span>
          )}
        </div>
        {!isEditMode && hasContent && (
          <span className="text-neutral-400 transition-transform">
            {viewFolded ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </span>
        )}
      </button>

      {isEditMode ? (
        <div className="px-4 pb-4">
          <textarea
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            rows={5}
            className="w-full resize-y rounded-lg border border-neutral-200 bg-neutral-50/50 px-3.5 py-2.5 text-dense font-normal leading-relaxed text-neutral-900 shadow-sm transition-all focus:bg-card focus-visible:shadow-focus"
            placeholder={placeholder}
          />
        </div>
      ) : hasContent ? (
        <div
          className={cn(
            "overflow-hidden transition-all duration-200",
            viewFolded ? "max-h-0" : "max-h-[2000px]"
          )}
        >
          <div className="border-t border-neutral-50 px-4 py-3">
            <div className="whitespace-pre-wrap text-dense font-normal leading-[1.7] text-neutral-700">
              <HighlightText text={displayValue} highlightQuery={highlightQuery} />
            </div>
          </div>
        </div>
      ) : !isEditMode ? (
        <div className="border-t border-neutral-50 px-4 py-3">
          <span className="text-dense font-normal italic text-neutral-400">
            {placeholder ?? "No data available"}
          </span>
        </div>
      ) : null}
    </div>
  );
};
